/* eslint-disable @typescript-eslint/no-explicit-any */
import { useEffect, useCallback } from 'react';
import useSWR, { SWRConfiguration } from 'swr';
import { useApolloClient, useQuery, ApolloClient, NormalizedCacheObject } from '@apollo/client';
import { isServer } from '@marriott/mi-apollo-client-utils';
import { fetchHQVPropertyInfo, HQVFetcherContext } from './hqvFetchers';
import { useConditionalPageContextSERP } from './useConditionalPageContext';
import { useHQVStore } from '../../modules/store/hqvZustandStore';
import { phoenixShopHQVPropertyInfoCall } from '../../organisms/HotelGalleryTitle/HQVQueries';
import {
  SWR_ERROR_RETRY_COUNT,
  SWR_ERROR_RETRY_INTERVAL,
  hqvLog,
  isHqvDebugEnabled,
} from '../../constants/lib/constants';

export interface UseHQVPropertyInfoOptions {
  enabled?: boolean;
  swrConfig?: SWRConfiguration;
}

export interface UseHQVPropertyInfoResult {
  propertyInfo: any;
  isLoading: boolean;
  isValidating: boolean;
  error: Error | undefined;
  refetch: () => Promise<any>;
}

/**
 * Custom hook for fetching HQV Property Info data
 * Supports both SWR mode (new) and Apollo useQuery mode (legacy)
 *
 * @param propertyId - The property ID to fetch info for (null to skip fetching)
 * @param options - Configuration options
 * @returns Property info data, loading state, error, and refetch function
 *
 * @example
 * ```tsx
 * const { propertyInfo, isLoading, error } = useHQVPropertyInfo(propertyId);
 *
 * if (isLoading) return <Spinner />;
 * if (error) return <Error />;
 * return <PropertyDetails data={propertyInfo} />;
 * ```
 */
export function useHQVPropertyInfo(
  propertyId: string | null,
  options: UseHQVPropertyInfoOptions = {}
): UseHQVPropertyInfoResult {
  const { enabled = true, swrConfig = {} } = options;

  // Check if SWR mode is enabled from the store
  const enableUseSWR = useHQVStore(state => state.hqvState.enableUseSWR);

  const client = useApolloClient();
  const { requestId, currentLocale } = useConditionalPageContextSERP();

  // Normalize locale format (convert underscore to hyphen: en_US -> en-US)
  // and use fallback locale to ensure consistent SWR keys across components
  const normalizedLocale = currentLocale?.replace('_', '-') || '';
  const localeForKey = normalizedLocale || 'en-US';

  const context: HQVFetcherContext = {
    requestId,
    locale: localeForKey,
  };

  // ============================================================================
  // SWR MODE
  // ============================================================================
  const shouldFetchSWR = enableUseSWR && enabled && !!propertyId;
  const swrKey = shouldFetchSWR ? ['hqv-property-info', propertyId, localeForKey] : null;

  const {
    data: swrData,
    error: swrError,
    isLoading: swrIsLoading,
    isValidating: swrIsValidating,
    mutate: swrMutate,
  } = useSWR(
    swrKey,
    async ([, id]: [string, string, string]) => {
      if (isHqvDebugEnabled()) {
        hqvLog.log('useHQVPropertyInfo - [SWR Mode] Fetcher executing for:', id);
      }
      const result = await fetchHQVPropertyInfo(client as ApolloClient<NormalizedCacheObject>, id, context);
      if (isHqvDebugEnabled()) {
        hqvLog.log('useHQVPropertyInfo - [SWR Mode] Fetcher completed for:', id, { hasData: !!result });
      }
      return result;
    },
    {
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
      dedupingInterval: 5000,
      errorRetryCount: SWR_ERROR_RETRY_COUNT,
      errorRetryInterval: SWR_ERROR_RETRY_INTERVAL,
      ...swrConfig,
    }
  );

  // ============================================================================
  // LEGACY MODE (Apollo useQuery)
  // ============================================================================
  const shouldFetchLegacy = !enableUseSWR && enabled && !!propertyId;

  const {
    data: legacyData,
    error: legacyError,
    loading: legacyLoading,
    refetch: legacyRefetch,
  } = useQuery(phoenixShopHQVPropertyInfoCall, {
    skip: !shouldFetchLegacy,
    fetchPolicy: isServer ? 'network-only' : 'cache-first',
    variables: {
      propertyId: propertyId,
    },
    context: {
      headers: {
        'x-request-id': requestId || '',
        'accept-language': localeForKey,
      },
    },
  });

  // Log legacy mode state
  useEffect(() => {
    if (isHqvDebugEnabled() && shouldFetchLegacy) {
      hqvLog.log('useHQVPropertyInfo - [Legacy Mode] Apollo state:', {
        propertyId,
        isLoading: legacyLoading,
        hasData: !!legacyData,
        hasError: !!legacyError,
      });
    }
  }, [shouldFetchLegacy, propertyId, legacyLoading, legacyData, legacyError]);

  // ============================================================================
  // UNIFIED RETURN
  // ============================================================================
  const refetch = useCallback(async () => {
    if (enableUseSWR) {
      hqvLog.log('useHQVPropertyInfo - [SWR Mode] Refetching for:', propertyId);
      return swrMutate();
    } else {
      hqvLog.log('useHQVPropertyInfo - [Legacy Mode] Refetching for:', propertyId);
      return legacyRefetch();
    }
  }, [enableUseSWR, propertyId, swrMutate, legacyRefetch]);

  // Return based on mode
  if (enableUseSWR) {
    return {
      propertyInfo: swrData,
      isLoading: swrIsLoading,
      isValidating: swrIsValidating,
      error: swrError,
      refetch,
    };
  } else {
    return {
      propertyInfo: legacyData,
      isLoading: legacyLoading,
      isValidating: false,
      error: legacyError as Error | undefined,
      refetch,
    };
  }
}
